package com.evoapps.evoshift.domain.usecase

import com.evoapps.evoshift.domain.model.workerId
import com.evoapps.evoshift.domain.model.BreakRules
import com.evoapps.evoshift.domain.model.ClockStamp
import com.evoapps.evoshift.domain.model.ShiftBreak
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.UnplannedKind
import com.evoapps.evoshift.domain.model.UnplannedStatus
import com.evoapps.evoshift.domain.model.needsClockReview
import kotlinx.datetime.TimeZone

/** Where an assigned shift of today stands, from its clock times and the workplace's local time. */
enum class AtWorkStatus {
    /** The planned start is still ahead and nobody clocked in. */
    NOT_STARTED,
    /** The planned start passed without a clock-in. */
    LATE,
    /** Clocked in, not out yet. */
    WORKING,
    /** SH-116: clocked in and on a break now. */
    ON_BREAK,
    /** Clocked in and out. */
    DONE
}

/**
 * One row of the dashboards' "Today at work" card (SH-72). Times are the shift's local ISO
 * date-times; [actualStart] / [actualEnd] are null until the person clocks in / out.
 */
data class AtWorkEntry(
    val shiftId: String,
    val rosterId: String,
    val date: String,
    val userId: String,
    val name: String,
    val plannedStart: String,
    val plannedEnd: String,
    val actualStart: String?,
    val actualEnd: String?,
    val status: AtWorkStatus,
    /** SH-106: a clock time outside the workplace's area or without a location, for the manager. */
    val locationFlag: ClockStamp? = null,
    /** SH-116: the shift's breaks so far, in order (the open one has no end). */
    val breaks: List<ShiftBreak> = emptyList(),
    /** SH-134: who was planned, when someone else worked or clocked in on the shift ([name] is who came). */
    val insteadOf: String? = null,
    /** SH-134: flagged or unplanned clock times waiting for a manager. */
    val toReview: Boolean = false
) {
    /** SH-116: the break going on, if any. */
    val openBreak: ShiftBreak? get() = BreakRules.open(breaks)

    /** SH-116: minutes of the breaks already over. */
    val endedBreakMinutes: Long get() = BreakRules.endedMinutes(breaks)
}

/** [now] is the workplace's local ISO date-time (shift times are local, compared as strings). */
fun Shift.atWorkStatusAt(now: String): AtWorkStatus {
    val start = actualTime?.start.orEmpty()
    val end = actualTime?.end.orEmpty()
    return when {
        start.isNotEmpty() && end.isNotEmpty() -> AtWorkStatus.DONE
        start.isNotEmpty() && BreakRules.open(breaks) != null -> AtWorkStatus.ON_BREAK
        start.isNotEmpty() -> AtWorkStatus.WORKING
        now < plannedTime.start -> AtWorkStatus.NOT_STARTED
        else -> AtWorkStatus.LATE
    }
}

/** Today's assigned shifts with their clock times and status, by planned start (then name). */
fun todayAtWork(shifts: List<Shift>, today: String, now: String, nameOf: (String) -> String): List<AtWorkEntry> =
    shifts.asSequence()
        .filter { it.date == today }
        .mapNotNull { shift ->
            // SH-134 / SH-128: who came (a colleague covering, or recorded as who worked) leads the row.
            val cover = shift.unplanned?.takeIf { it.kind == UnplannedKind.COVER && it.status != UnplannedStatus.REJECTED }
            val planned = shift.userId
            // SH-236: nobody, when a manager recorded that nobody came.
            val userId = cover?.userId ?: shift.workerId ?: return@mapNotNull null
            if (shift.unplanned?.kind == UnplannedKind.EXTRA && shift.unplanned.status == UnplannedStatus.REJECTED) return@mapNotNull null
            AtWorkEntry(
                shiftId = shift.id,
                rosterId = shift.rosterId,
                date = shift.date,
                userId = userId,
                name = cover?.userName?.takeIf { it.isNotBlank() } ?: nameOf(userId),
                insteadOf = planned?.takeIf { it != userId }?.let(nameOf),
                toReview = shift.needsClockReview,
                plannedStart = shift.plannedTime.start,
                plannedEnd = shift.plannedTime.end,
                actualStart = shift.actualTime?.start?.takeIf { it.isNotEmpty() },
                actualEnd = shift.actualTime?.end?.takeIf { it.isNotEmpty() },
                status = shift.atWorkStatusAt(now),
                locationFlag = shift.clockAudit?.locationFlag,
                breaks = shift.breaks
            )
        }
        .sortedWith(compareBy<AtWorkEntry>({ it.plannedStart }, { it.name }))
        .toList()

/** The workplace's time zone setting, or null when it is missing or not a known zone. */
fun timeZoneOrNull(id: String?): TimeZone? =
    id?.takeIf { it.isNotBlank() }?.let { runCatching { TimeZone.of(it) }.getOrNull() }
